import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Mail, RotateCcw } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function Signup() {
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState(null);
  const [successEmail, setSuccessEmail] = useState(null);
  const [resendMessage, setResendMessage] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    businessName: '',
    email: '',
    password: '',
  });

  const emailRedirectTo = `${window.location.origin}/login?confirmed=true`;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo,
          data: {
            full_name: formData.fullName,
            business_name: formData.businessName,
          },
        },
      });

      if (signUpError) throw signUpError;

      // Check if email confirmation is required
      if (data?.user?.identities?.length === 0) {
        setError('An account with this email already exists. Please log in instead.');
        setLoading(false);
        return;
      }

      if (data?.session) {
        await supabase.auth.signOut();
      }

      setSuccessEmail(formData.email);
      setLoading(false);
    } catch (err) {
      setError(err.message || 'An error occurred during signup');
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!successEmail) return;

    try {
      setResending(true);
      setError(null);
      setResendMessage(null);

      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: successEmail,
        options: {
          emailRedirectTo,
        },
      });

      if (resendError) throw resendError;

      setResendMessage('Confirmation email sent again. Check your inbox and spam folder.');
    } catch (err) {
      setError(err.message || 'Unable to resend the confirmation email.');
    } finally {
      setResending(false);
    }
  };

  if (successEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-md bg-emerald-700 text-sm font-bold text-white">
              GQ
            </div>
            <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-950">Account created</h1>
            <p className="mt-2 text-slate-600">Check your email to activate GreenQuote.</p>
          </div>

          <div className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 p-4">
            <div className="flex gap-3">
              <Mail className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
              <div>
                <p className="text-sm font-medium text-emerald-950">
                  We sent a confirmation link to:
                </p>
                <p className="mt-1 break-all text-sm text-emerald-800">{successEmail}</p>
              </div>
            </div>
          </div>

          <div className="mt-5 space-y-3 text-sm text-slate-600">
            <p>After you confirm your email, the link will bring you back to GreenQuote to sign in.</p>
            <p>If you do not see it within a minute, check your spam or promotions folder.</p>
          </div>

          {resendMessage && (
            <Alert className="mt-5 border-emerald-200 bg-emerald-50 text-emerald-900">
              <AlertDescription>{resendMessage}</AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive" className="mt-5">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="mt-6 grid gap-3">
            <Button asChild className="w-full bg-emerald-700 hover:bg-emerald-800 text-white">
              <Link to="/login">Go to Login</Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleResendEmail}
              disabled={resending}
              className="w-full"
            >
              <RotateCcw className="h-4 w-4" />
              {resending ? 'Sending...' : 'Resend Confirmation Email'}
            </Button>
          </div>

          <div className="mt-6 border-t border-slate-200 pt-5 text-center">
            <button
              type="button"
              onClick={() => {
                setSuccessEmail(null);
                setResendMessage(null);
                setError(null);
              }}
              className="text-sm font-medium text-emerald-700 hover:text-emerald-800"
            >
              Use a different email
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-md bg-emerald-700 text-sm font-bold text-white">
            GQ
          </div>
          <h1 className="text-2xl font-semibold text-slate-950 mb-2">GreenQuote Pro</h1>
          <h2 className="text-lg font-medium text-slate-700">Create Account</h2>
          <p className="text-slate-500 mt-2">Get started with your lawn care business</p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="fullName">Full Name</Label>
            <Input
              id="fullName"
              name="fullName"
              type="text"
              required
              value={formData.fullName}
              onChange={handleChange}
              placeholder="John Doe"
              disabled={loading}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="businessName">Business Name</Label>
            <Input
              id="businessName"
              name="businessName"
              type="text"
              required
              value={formData.businessName}
              onChange={handleChange}
              placeholder="Green Lawn Services"
              disabled={loading}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              disabled={loading}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              name="password"
              type="password"
              required
              minLength={6}
              value={formData.password}
              onChange={handleChange}
              placeholder="Min. 6 characters"
              disabled={loading}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Must be at least 6 characters long
            </p>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
          >
            {loading ? 'Creating Account...' : 'Sign Up'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-semibold text-emerald-700 hover:text-emerald-800"
            >
              Log in
            </Link>
          </p>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
          <p>By signing up, you agree to our Terms of Service and Privacy Policy</p>
        </div>
      </div>
    </div>
  );
}
